import React from 'react';
import { useTranslation } from 'react-i18next';
import { TradeBookingForm, TradeType, SettlementType, FixingOffset } from '../../types/tradeBooking';
import { formatAmountDisplay, formatPriceDisplay } from '../../utils/amountFormatter';

interface ConfirmationDialogProps {
  isOpen: boolean;
  form: TradeBookingForm;
  actionType: 'draft' | 'submit' | 'approval' | 'update';
  locale: string;
  onConfirm: () => void;
  onCancel: () => void;
}

/** Format a Date as DD/MM/YYYY */
const fmtDate = (d: Date | null | undefined): string => {
  if (!d) return '—';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

/** Wrap known dynamic values in highlight spans within a text string */
const hl = (text: string, values: string[]): React.ReactNode => {
  const valid = values.filter(v => v && v !== '—');
  if (valid.length === 0) return text;
  // Sort longest-first so composite values (e.g. "CLP 1.722.000,00") match before
  // their sub-parts (e.g. "CLP")
  const sorted = [...valid].sort((a, b) => b.length - a.length);
  const escaped = sorted.map(v => v.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const regex = new RegExp(`(${escaped.join('|')})`, 'g');
  const parts = text.split(regex);
  const valueSet = new Set(sorted);
  return (
    <>
      {parts.map((part, i) =>
        valueSet.has(part)
          ? <span key={i} className="prose-highlight">{part}</span>
          : part
      )}
    </>
  );
};

const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  isOpen,
  form,
  actionType,
  locale,
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslation();

  if (!isOpen) return null;

  const getTitle = () => {
    switch (actionType) {
      case 'draft':
        return t('tradeBooking.confirmation.draftTitle');
      case 'approval':
        return t('tradeBooking.confirmation.approvalTitle');
      case 'update':
        return t('tradeBooking.confirmation.updateTitle');
      default:
        return t('tradeBooking.confirmation.title');
    }
  };

  const isBuy = form.direction === 'BUY';
  const isForward = form.tradeType === TradeType.FORWARD;
  const isNDF = isForward && form.settlementType === SettlementType.COMPENSACION;
  const isPhysical = isForward && form.settlementType === SettlementType.ENTREGA_FISICA;
  const isAsiatico = isForward && form.isAverageRateForward;
  const isForwardStarting = isForward && form.isForwardStarting;

  const amt = form.amount !== null ? formatAmountDisplay(form.amount, locale, 2) : '—';
  const px = form.price !== null && form.price !== 0 ? formatPriceDisplay(form.price, locale, 4) : '—';
  const contra = form.contraAmount !== null && form.contraAmount !== 0 ? formatAmountDisplay(form.contraAmount, locale, 2) : '—';
  const hasRateBreakdown = isForward && !isForwardStarting && form.spotPrice !== null && form.forwardPoints !== null;

  const paragraphs: React.ReactNode[] = [];

  // --- Paragraph 1: Core trade ---
  const verb = isBuy
    ? t('tradeBooking.confirmation.prose.buying')
    : t('tradeBooking.confirmation.prose.selling');
  const tradeTypeFragment = isForward && form.maturityDays
    ? ` ${t('tradeBooking.confirmation.prose.atMaturity', { days: form.maturityDays })}`
    : !isForward
    ? ` ${t('tradeBooking.confirmation.prose.spot')}`
    : '';

  if (isForwardStarting) {
    // Forward Starting: price unknown at booking
    const toBeFixed = t('tradeBooking.forwardStarting.toBeFixed');
    const fwdPts = form.forwardPoints !== null ? formatPriceDisplay(form.forwardPoints, locale, 4) : '—';
    const p1 = `${verb} ${form.currency1} ${amt}${tradeTypeFragment} ${t('tradeBooking.confirmation.prose.against')} ${form.currency2}. ${t('tradeBooking.confirmation.prose.priceToBeFixed')}.`;
    paragraphs.push(hl(p1, [
      `${form.currency1} ${amt}`,
      form.currency2,
      ...(form.maturityDays ? [String(form.maturityDays)] : []),
    ]));

    // Forward Starting explanation paragraph
    const fixingRef = t(`tradeBooking.fixingReferences.${form.fixingReference}`);
    const valueDateStr = fmtDate(form.valueDate);
    const isAveragedEntry = form.forwardStartingFixingSchedule && form.forwardStartingFixingSchedule.length > 1;
    if (isAveragedEntry) {
      const fixingDates = form.forwardStartingFixingSchedule
        .filter(e => e.date)
        .sort((a, b) => a.date.localeCompare(b.date))
        .map(e => {
          const [y, m, d] = e.date.split('-');
          return `${d}/${m}/${y}`;
        })
        .join(', ');
      const p1b = t('tradeBooking.confirmation.prose.forwardStartingAveraged', {
        fixingRef,
        dates: fixingDates,
        count: form.forwardStartingFixingSchedule.length,
        fwdPts,
      });
      paragraphs.push(hl(p1b, [fixingRef, fixingDates, fwdPts]));
    } else {
      const p1b = t('tradeBooking.confirmation.prose.forwardStarting', {
        fixingRef,
        valueDate: valueDateStr,
        fwdPts,
      });
      paragraphs.push(hl(p1b, [fixingRef, valueDateStr, fwdPts]));
    }
  } else {
    const p1 = `${verb} ${form.currency1} ${amt}${tradeTypeFragment} ${t('tradeBooking.confirmation.prose.against')} ${form.currency2} ${t('tradeBooking.confirmation.prose.atPrice')} ${px} (${t('tradeBooking.confirmation.prose.contraAmount').toLowerCase()}: ${form.currency2} ${contra}).`;
    paragraphs.push(hl(p1, [
      `${form.currency1} ${amt}`,
      `${form.currency2} ${contra}`,
      form.currency2,
      px,
      ...(form.maturityDays ? [String(form.maturityDays)] : []),
    ]));
  }

  // --- Paragraph 1b: Rate breakdown (forwards with spot/points only, not Forward Starting) ---
  if (hasRateBreakdown) {
    const spot = formatPriceDisplay(form.spotPrice!, locale, 4);
    const pts = formatPriceDisplay(form.forwardPoints!, locale, 4);
    const p1b = t('tradeBooking.confirmation.prose.rateBreakdown', { spot, points: pts, allIn: px });
    paragraphs.push(hl(p1b, [spot, pts, px]));
  }

  // --- Paragraph 2: Dates ---
  const tradeDateStr = fmtDate(form.tradeDate);
  const valueDateStr = fmtDate(form.valueDate);
  const paymentDateStr = fmtDate(form.paymentDate);
  if (isForward) {
    const expiryDateStr = fmtDate(form.expiryDate);
    const p2 = t('tradeBooking.confirmation.prose.datesForward', {
      tradeDate: tradeDateStr,
      valueDate: valueDateStr,
      expiryDate: expiryDateStr,
      paymentDate: paymentDateStr,
    });
    paragraphs.push(hl(p2, [tradeDateStr, valueDateStr, expiryDateStr, paymentDateStr]));
  } else {
    const p2 = t('tradeBooking.confirmation.prose.datesSpot', {
      tradeDate: tradeDateStr,
      valueDate: valueDateStr,
      paymentDate: paymentDateStr,
    });
    paragraphs.push(hl(p2, [tradeDateStr, valueDateStr, paymentDateStr]));
  }

  // --- Paragraph 3: Settlement (Forward only) ---
  if (isNDF) {
    const fixingRef = t(`tradeBooking.fixingReferences.${form.fixingReference}`);
    const offsetText = form.fixingOffset === FixingOffset.ZERO
      ? t('tradeBooking.confirmation.prose.noOffset')
      : `offset T${form.fixingOffset}`;
    const p3 = t('tradeBooking.confirmation.prose.settlementNDF', {
      currency: form.settlementCurrency,
      fixingRef,
      offset: offsetText,
    });
    paragraphs.push(hl(p3, [form.settlementCurrency, fixingRef, offsetText]));
  } else if (isPhysical) {
    paragraphs.push(t('tradeBooking.confirmation.prose.settlementPhysical'));
  }

  // --- Paragraph 4: Asiático fixing schedule ---
  if (isAsiatico && form.averageRateFixingSchedule.length > 0) {
    const dateList = form.averageRateFixingSchedule.map(entry => {
      if (!entry.date) return '—';
      const [y, m, d] = entry.date.split('-');
      return `${d}/${m}/${y}`;
    });
    const datesStr = dateList.join(', ');
    const p4 = t('tradeBooking.confirmation.prose.asiatico', { dates: datesStr });
    paragraphs.push(hl(p4, dateList));
  }

  // --- Paragraph 5: Parties ---
  const ourParty = form.ourParty || '—';
  const counterparty = form.counterparty || '—';
  const p5 = t('tradeBooking.confirmation.prose.parties', { ourParty, counterparty });
  paragraphs.push(hl(p5, [ourParty, counterparty]));

  // --- Paragraph 6: Notes ---
  if (form.notes?.trim()) {
    const notes = form.notes.trim();
    const p6 = t('tradeBooking.confirmation.prose.notes', { notes });
    paragraphs.push(hl(p6, [notes]));
  }

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal-dialog confirmation-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{getTitle()}</h2>
        </div>

        <div className="modal-body">
          {actionType !== 'draft' && (
            <div className="trade-summary-prose">
              {paragraphs.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onCancel}>
            {t('tradeBooking.confirmation.cancel')}
          </button>
          <button type="button" className="btn btn-primary" onClick={onConfirm}>
            {t('tradeBooking.confirmation.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationDialog;
